import { EmulateDonationButton } from "@/Components/EmulateDonationButton/EmulateDonationButton";
import { useAuth } from "@/hooks/useAuth";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { useSocket } from "@/hooks/useSocket";
import { useSocketCallback } from "@/hooks/useSocketCallback";
import { cumulativeLevels, getLevelFromDonationSum, trainLevels } from "@/lib/DonoTrainUtil";
import { msToTimeStr } from "@/lib/utils";
import { DonoTrain, DonoTrainSettings } from "@ailenax-overlay/shared";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { FaRegClipboard, FaTrash } from "react-icons/fa";

interface DonoTrainConfigCardProps {
    train: DonoTrain;
    settings: DonoTrainSettings;
    saveSettings: (settings: DonoTrainSettings) => void;
}

export const DonoTrainConfigCard: FC<DonoTrainConfigCardProps> = ({ train, settings: initialSettings, saveSettings }) => {
    const [settings, setSettings] = useState<DonoTrainSettings>(initialSettings);
    const [record, setRecord] = useState<number>(0);
    const { token } = useAuth();
    const [,copyUrl] = useCopyToClipboard();
    const socket = useSocket();
    const [donatorName, setDonatorName] = useLocalStorage("emulateDonatorName", "Tester");
    const overlayUrl = import.meta.env.VITE_OVERLAY_URL;

    if (donatorName === "Biertornado" && !overlayUrl.toLowerCase().includes("ailenax")) {
        setDonatorName("Tester");
    }

    useSocketCallback("get-donotrain-record", setRecord);

    useEffect(() => {
        setSettings(initialSettings);
    }, [initialSettings]);

    const addGoalRow = () => {
        setSettings(prev => {
            const goals = [...prev.goals];
            goals.push({
                level: goals.length > 0 ? goals.at(-1)!.level + 1 : 1,
                goal: "",
                message: "",
            });

            return {
                ...prev,
                goals
            };
        })
    }

    const deleteGoalRow = (idx: number) => {
        setSettings(prev => {
            const goals = [...prev.goals];
            goals.splice(idx, 1);

            return {
                ...prev,
                goals
            };
        })
    }

    const setGoalLevel = (idx: number, level: number) => {
        setSettings(prev => {
            const goals = [...prev.goals];
            goals[idx] = {
                ...goals[idx],
                level
            };
            return {
                ...prev,
                goals
            };
        })
    }

    const setGoalText = (idx: number, text: string) => {
        setSettings(prev => {
            const goals = [...prev.goals];
            goals[idx] = {
                ...goals[idx],
                goal: text
            };
            return {
                ...prev,
                goals
            };
        })
    }

    const setGoalMessage = (idx: number, message: string) => {
        setSettings(prev => {
            const goals = [...prev.goals];
            goals[idx] = {
                ...goals[idx],
                message: message
            };
            return {
                ...prev,
                goals
            };
        })
    }

    const setTime = (key: keyof DonoTrainSettings["durations"], value: string) => {
        const [hours, minutes, seconds] = value.split(":").map(v => Number.parseInt(v, 10));
        const ms = (hours * 3600 + minutes * 60 + seconds) * 1000;
        setSettings(prev => {
            return {
                ...prev,
                durations: {
                    ...prev.durations,
                    [key]: ms
                }
            };
        });
    }

    const setField = <F extends keyof DonoTrainSettings>(field: F, value: DonoTrainSettings[F]) => {
        setSettings(prev => {
            return {
                ...prev,
                [field]: value
            };
        });
    }

    const txtRecord = useMemo(() => {
        const recordLevel = getLevelFromDonationSum(record);
        const recordProgress = record - (cumulativeLevels[recordLevel - 2] ?? 0);
        const recordRequired = trainLevels[recordLevel - 1] ?? trainLevels.at(-1);
        const recordPercent = 100 * recordProgress / recordRequired;
        return `Level: ${recordLevel}, ${recordPercent.toFixed(0)}%`;
    }, [record]);

    const putOnCooldown = useCallback(() => {
        socket.emit("put-donotrain-on-cooldown");
    }, [socket]);

    const putOnReady = useCallback(() => {
        socket.emit("put-donotrain-on-ready");
    }, [socket]);

    const toggleBoxMode = useCallback(() => {
        socket.emit("toggle-donotrain-box-mode");
    }, [socket]);

    return (
        <div className="col-span-12">
            <form className="flex flex-col max-w-5xl mx-auto dark:bg-gray-800 p-5 rounded-3xl">
                <h2 className="text-3xl font-bold self-center">Train Settings</h2>
                <div className="flex flex-row gap-6 mt-5">
                    <div className="relative z-0 w-full mb-5 group">
                        <input type="time" name="floating_primed" id="floating_primed" className="block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-blue-600 peer" value={msToTimeStr(settings.durations.primed)} onChange={e => setTime("primed", e.target.value)} step={1} required />
                        <label htmlFor="floating_primed" className="peer-focus:font-medium absolute text-sm text-orange-400 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:start-0 rtl:peer-focus:translate-x-1/4 peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6">Primed</label>
                    </div>
                    <div className="relative z-0 w-full mb-5 group">
                        <input type="time" name="floating_inprogress" id="floating_inprogress" className="block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-blue-600 peer" value={msToTimeStr(settings.durations.inProgress)} onChange={e => setTime("inProgress", e.target.value)} step={1} required />
                        <label htmlFor="floating_inprogress" className="peer-focus:font-medium absolute text-sm text-teal-500 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:start-0 rtl:peer-focus:translate-x-1/4 peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6">Progress</label>
                    </div>
                    <div className="relative z-0 w-full mb-5 group">
                        <input type="time" name="floating_cooldown" id="floating_cooldown" className="block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-blue-600 peer" value={msToTimeStr(settings.durations.cooldown)} onChange={e => setTime("cooldown", e.target.value)} step={1} required />
                        <label htmlFor="floating_cooldown" className="peer-focus:font-medium absolute text-sm text-red-700 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:start-0 rtl:peer-focus:translate-x-1/4 peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6">Cooldown</label>
                    </div>
                </div>
                { import.meta.env.VITE_FEATURE_LEADERBOARD === true && (
                    <div className="flex flex-row gap-6">
                        <div className="relative z-0 w-full mb-5 group">
                            <input type="text" name="leaderbord_text_left" id="leaderbord_text_left" className="block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-blue-600 peer" value={settings.leaderboardTextLeft ?? ""} onChange={e => setField("leaderboardTextLeft", e.target.value)} />
                            <label htmlFor="leaderbord_text_left" className="peer-focus:font-medium absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:start-0 rtl:peer-focus:translate-x-1/4 peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6">Leaderboard Text Left</label>
                        </div>
                        <div className="relative z-0 w-full mb-5 group">
                            <input type="text" name="leaderbord_text_right" id="leaderbord_text_right" className="block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-blue-600 peer" value={settings.leaderboardTextRight ?? ""} onChange={e => setField("leaderboardTextRight", e.target.value)} />
                            <label htmlFor="leaderbord_text_right" className="peer-focus:font-medium absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:start-0 rtl:peer-focus:translate-x-1/4 peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6">Leaderboard Text Right</label>
                        </div>
                    </div>
                )}
                <div className="flex flex-row gap-6 mb-10">
                    <div className="relative z-0 w-full">Donation-Train Record: { txtRecord }</div>
                </div>
                { settings.goals.map((g, i) => (
                    <div key={`goal-${i}`} className="flex flex-row gap-6">
                        <div className="relative z-0 flex-1/6 mb-5 group">
                            <input type="number" name={`floating_goal_level_${g.level}`} id={`floating_goal_level_${g.level}`} className="block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-blue-600 peer" placeholder="" value={g.level} onChange={e => setGoalLevel(i, Number.parseInt(e.target.value, 10))} required />
                            <label htmlFor={`floating_goal_level_${g.level}`} className="peer-focus:font-medium absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:start-0 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6">Level</label>
                        </div>
                        <div className="relative z-0 flex-2/6 mb-5 group">
                            <input type="text" name={`floating_goal_${g.level}`} id={`floating_goal_${g.level}`} className="block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-blue-600 peer" placeholder="" value={g.goal} onChange={e => setGoalText(i, e.target.value)} required />
                            <label htmlFor={`floating_goal_${g.level}`} className="peer-focus:font-medium absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:start-0 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6">Goal</label>
                        </div>
                        <div className="relative z-0 flex-2/6 mb-5 group">
                            <input type="text" name={`floating_goal_message_${g.level}`} id={`floating_goal_message_${g.level}`} className="block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-blue-600 peer" placeholder="" value={g.message} onChange={e => setGoalMessage(i, e.target.value)} required />
                            <label htmlFor={`floating_goal_message_${g.level}`} className="peer-focus:font-medium absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:start-0 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6">Chat-Bot Level Reached Message</label>
                        </div>
                        <div className="relative z-0 flex-1/6 mb-5 group">
                            <button type="button" className="text-white bg-red-700 hover:bg-red-800 focus:ring-4 focus:outline-none focus:ring-red-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-red-600 dark:hover:bg-red-700 dark:focus:ring-red-800" onClick={() => deleteGoalRow(i)}><FaTrash /></button>
                        </div>
                    </div>
                ))}
            </form>
            <form className="flex flex-col max-w-5xl mx-auto mt-5 dark:bg-gray-800 px-5 p-5 rounded-3xl">
                <div className="flex flex-row items-center justify-center gap-2">
                    <button type="button" className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800" onClick={() => saveSettings(settings)}>Save</button>
                    { train.status !== "cooldown" && (
                        <button type="button" className="text-white bg-red-700 hover:bg-red-800 focus:ring-4 focus:outline-none focus:ring-red-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-red-600 dark:hover:bg-red-700 dark:focus:ring-red-800" onClick={putOnCooldown}>Cooldown</button>
                    )}
                    { train.status !== "ready" && (
                        <button type="button" className="text-white bg-green-700 hover:bg-green-800 focus:ring-4 focus:outline-none focus:ring-green-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-green-600 dark:hover:bg-green-700 dark:focus:ring-green-800" onClick={putOnReady}>Ready</button>
                    )}
                    <button type="button" className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800" onClick={toggleBoxMode}>
                        Show { settings.boxMode === undefined || settings.boxMode === "goal" ? "Record" : "Goal" } in Box
                    </button>
                    <button type="button" className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800" onClick={addGoalRow}>
                        Add Goal Row
                    </button>
                </div>
                { token && (
                    <div className="flex flex-row items-center justify-center gap-2 my-2">
                            <button type="button" className="flex flex-row gap-2 items-center justify-center text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800" onClick={() => copyUrl(`${import.meta.env.VITE_OVERLAY_URL}/obs-widgets/donotrain?token=${token}`)}>
                                <FaRegClipboard size={20}/> Copy Browser-Source URL
                            </button>
                            <button type="button" className="flex flex-row gap-2 items-center justify-center text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800" onClick={() => copyUrl(`${import.meta.env.VITE_OVERLAY_URL}/obs-widgets/donotrain?token=${token}&config`)}>
                                <FaRegClipboard size={20}/> Copy Browser-Source URL (Config Mode)
                            </button>
                            <button type="button" className="flex flex-row gap-2 items-center justify-center text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800" onClick={() => copyUrl(`${import.meta.env.VITE_OVERLAY_URL}/obs-widgets/donotrain-goals?token=${token}`)}>
                                <FaRegClipboard size={20}/> Copy Goals Browser-Source URL
                            </button>
                    </div>
                )}
                <div className="flex flex-row items-center justify-center gap-2 my-2">
                    <input type="text" className="block py-2.5 px-0 w-1/3 text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-blue-600 peer text-center" placeholder="Donator Name" value={donatorName} onChange={e => setDonatorName(e.target.value)} />
                </div>
                <div className="flex flex-row items-center justify-center gap-2 my-2">
                    <EmulateDonationButton id="1" donator={donatorName} fallbackAmount={1.11} />
                    <EmulateDonationButton id="2" donator={donatorName} fallbackAmount={11.11} />
                    <EmulateDonationButton id="3" donator={donatorName} fallbackAmount={13.37} />
                    <EmulateDonationButton id="4" donator={donatorName} fallbackAmount={22.22} />
                    <EmulateDonationButton id="5" donator={donatorName} fallbackAmount={55.55} />
                    <EmulateDonationButton id="6" donator={donatorName} fallbackAmount={111.11} />
                </div>
            </form>
        </div>
    )
}